import {
  Tooltip,
  Badge,
  Box,
  Dialog,
  IconButton,
  DialogContent,
  DialogTitle,
  Button,
  DialogActions,
  Typography,
} from '@mui/material';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectTimeRangeDT } from '@/features/energyxplorer/redux/timeRangeSlice';
import {
  convertTimestampsSummaryToDateTimes,
  isOutOfBounds,
  isValidTimestampsSummary,
} from '@/features/energyxplorer/utils/timestampsSummaryHelpers';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { useGetEnergyGroupByIdQuery } from '@/api/recalcV2/endpoints/energyGroupsApiSlice';
import EnergyRichTreeView from '@/features/energyxplorer/components/EnergyRichTreeView';
import { useMemo, useState } from 'react';
import { useSeuDetailedTimestampsSummary } from '@/features/energyxplorer/hooks/useSeuDetailedTimestampsSummary';

export default function TimestampSummarySeuWarningIcon({ seu, energyGroupId }) {
  const { t } = useTranslation('energyxplorer');
  const timeRange = useSelector(selectTimeRangeDT);
  const selectedAccount = useSelector(getSelectedAccount);
  const [isOpen, setIsOpen] = useState(false);
  const { data: energyGroup } = useGetEnergyGroupByIdQuery(
    {
      energyGroupId: energyGroupId,
      accountId: selectedAccount.AccountId,
    },
    {
      skip: !energyGroupId,
    },
  );

  const detailedSummary = useSeuDetailedTimestampsSummary(
    selectedAccount?.AccountId,
    energyGroup,
    seu,
  );

  const displayWarning = useMemo(() => {
    if (!detailedSummary?.[0]?.energySummary) return null;
    const timestampsSummary = convertTimestampsSummaryToDateTimes(
      detailedSummary?.[0]?.energySummary?.TimestampsSummary,
    );
    if (!timestampsSummary) return null;
    if (!isValidTimestampsSummary(timestampsSummary)) return null;

    return isOutOfBounds(timestampsSummary, timeRange);
  }, [detailedSummary, timeRange]);

  if (!displayWarning) return null;

  return (
    <>
      <Tooltip
        title={
          <>
            <Box whiteSpace={'pre-line'}>
              {t('common.timestamp_summary_warning')}
            </Box>
          </>
        }
      >
        <Badge
          badgeContent={<WarningRoundedIcon fontSize="small" color="warning" />}
          sx={{ ml: 1, top: 2 }}
        >
          <IconButton onClick={() => setIsOpen(true)}>
            <CalendarTodayRoundedIcon fontSize="small" />
          </IconButton>
        </Badge>
      </Tooltip>

      <Dialog maxWidth="lg" open={isOpen} onClose={() => setIsOpen(false)}>
        <DialogTitle>{t('data_overview.title')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1">
            {t('data_overview.subtitle')}
          </Typography>
          <EnergyRichTreeView
            items={detailedSummary}
            defaultExpanded={[detailedSummary[0].energyTreeNodeId]}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsOpen(false)} variant="text">
            Luk
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
